<template>
  <div>
    <div class="t-t-image-form">
      <menu-content-container
        :chip="$t('forms.image_properties')"
        chip-secondary
        :border-full="!src"
      >
        <content-row justify-between :margin-top="16" class="flex-wrap gap-y-2">
          <ImageButtons v-model="src" :show-replace="!disabledSrc" />
          <TButton
            data-testid="image-set-dynamic"
            @click="setDynamic = !setDynamic"
          >
            <template #icon>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.4375 12.5625C15.7422 12.5625 16 12.8203 16 13.125C16 13.4531 15.7422 13.6875 15.4375 13.6875H7.65625C7.42188 14.4609 6.69531 15 5.875 15C4.82031 15 4 14.1797 4 13.125C4 12.0938 4.82031 11.25 5.875 11.25C6.69531 11.25 7.42188 11.8125 7.65625 12.5625H15.4375ZM5.125 13.125C5.125 13.5469 5.45312 13.875 5.875 13.875C6.27344 13.875 6.625 13.5469 6.625 13.125C6.625 12.7266 6.27344 12.375 5.875 12.375C5.45312 12.375 5.125 12.7266 5.125 13.125ZM12.3203 7.3125C12.5547 6.5625 13.2812 6 14.125 6C15.1562 6 16 6.84375 16 7.875C16 8.92969 15.1562 9.75 14.125 9.75C13.2812 9.75 12.5547 9.21094 12.3203 8.4375H4.5625C4.23438 8.4375 4 8.20312 4 7.875C4 7.57031 4.23438 7.3125 4.5625 7.3125H12.3203ZM14.125 8.625C14.5234 8.625 14.875 8.29688 14.875 7.875C14.875 7.47656 14.5234 7.125 14.125 7.125C13.7031 7.125 13.375 7.47656 13.375 7.875C13.375 8.29688 13.7031 8.625 14.125 8.625Z"
                  fill="currentColor"
                />
              </svg>
            </template>
            {{ $t("forms.image_set_dynamic") }}
          </TButton>
        </content-row>

        <content-row v-if="setDynamic">
          <t-auto-complete
            v-model="src"
            data-testid="image-dynamic-src"
            :label="`${$t('forms.image_url')} / ${$t('forms.dynamic_image')}`"
            placeholder="Some placeholder"
            :has-icon="true"
            :flat-merge-tags="mergeTagsStore.flatMergeTags"
            :grouped-merge-tags="mergeTagsStore.mergeTagsForInputs"
            :smart-merge-tags="mergeTagsStore.smartMergeTags"
            :disabled="disabledSrc"
            @on-blur="mutationEmptyString = ''"
          ></t-auto-complete>
        </content-row>
      </menu-content-container>

      <menu-content-container v-if="src" border-full>
        <content-row>
          <t-auto-complete
            v-model="href"
            data-testid="image-link"
            :label="$t('forms.image_link')"
            placeholder="http://"
            :has-icon="true"
            :flat-merge-tags="mergeTagsStore.flatMergeTags"
            :grouped-merge-tags="mergeTagsStore.mergeTagsForInputs"
            :smart-merge-tags="mergeTagsStore.smartMergeTags"
            :disabled="disabledHref"
            @on-blur="parseHref"
          ></t-auto-complete>
        </content-row>

        <content-row>
          <t-auto-complete
            v-model="title"
            data-testid="image-title"
            :label="$t('forms.image_title')"
            :placeholder="$t('forms.image_title')"
            :has-icon="true"
            :flat-merge-tags="mergeTagsStore.flatMergeTags"
            :grouped-merge-tags="mergeTagsStore.mergeTagsForInputs"
            :smart-merge-tags="mergeTagsStore.smartMergeTags"
            :disabled="disabledTitle"
            @on-blur="mutationEmptyString = ''"
          ></t-auto-complete>
        </content-row>

        <content-row>
          <t-auto-complete
            v-model="alt"
            data-testid="image-alt"
            :label="$t('forms.image_alt')"
            :placeholder="$t('forms.image_alt')"
            :has-icon="true"
            :flat-merge-tags="mergeTagsStore.flatMergeTags"
            :grouped-merge-tags="mergeTagsStore.mergeTagsForInputs"
            :smart-merge-tags="mergeTagsStore.smartMergeTags"
            :disabled="disabledAlt"
            @on-blur="mutationEmptyString = ''"
          ></t-auto-complete>
        </content-row>
      </menu-content-container>

      <menu-content-container
        v-if="src"
        :header="$t('forms.image_size')"
        :header-margin-bottom="6"
      >
        <content-row :margin-top="16">
          <t-slider
            v-model="width"
            :max="containerWidth"
            :min="30"
            unit="px"
            data-testid="image-size-slider"
            :disabled="disabledWidth"
          ></t-slider>
        </content-row>
      </menu-content-container>

      <menu-content-container border-full>
        <TLabel
          :changed="align.changed"
          :tooltip="$t('forms.reset_value')"
          data-testid="align-label"
          @reset="align.reset"
          >{{ $t("forms.image_align") }}</TLabel
        >
        <content-row justify-between :margin-top="12">
          <TButtonGroup>
            <TGroupButton
              data="left"
              small
              data-testid="align-left"
              :active="align.model === 'left'"
              :disabled="disabledAlign"
              @button-clicked="align.model = 'left'"
            >
              <AligmentLeft />
            </TGroupButton>
            <TGroupButton
              data="center"
              small
              data-testid="align-center"
              :active="align.model === 'center'"
              :disabled="disabledAlign"
              @button-clicked="align.model = 'center'"
            >
              <AligmentCenter />
            </TGroupButton>
            <TGroupButton
              data="right"
              small
              data-testid="align-right"
              :active="align.model === 'right'"
              :disabled="disabledAlign"
              @button-clicked="align.model = 'right'"
            >
              <AligmentRight />
            </TGroupButton>
          </TButtonGroup>

          <t-switch
            v-model="fluidOnMobile.model"
            data-testid="fluid-on-mobile"
            :label="$t('forms.fluid_on_mobile')"
            :disabled="disabledFluid"
            :changed="fluidOnMobile.changed"
            :reset-tooltip="$t('forms.reset_value')"
            @reset="fluidOnMobile.reset"
          ></t-switch>
        </content-row>
      </menu-content-container>

      <borderRadiusForm
        data-testid="image-border-radius"
        :label="$t('forms.border_radius')"
        :min-y="0"
        :max-y="containerWidth"
        :min-x="0"
        :max-x="containerWidth"
        :changed="borderRadius.changed"
        :default-value="borderRadius.defaultValue"
        @reset="borderRadius.reset"
      ></borderRadiusForm>

      <padding-form
        data-testid="image-margin"
        :label="$t('forms.image_margin')"
        :disabled="disabledPadding"
        :changed="padding.changed"
        :default-value="padding.defaultValue"
        @reset="padding.reset"
      ></padding-form>

      <menu-content-container :border-bottom="false">
        <content-row>
          <hide-on-mobile></hide-on-mobile>
        </content-row>

        <content-row :margin-top="16" :margin-bottom="20">
          <hide-on-desktop></hide-on-desktop>
        </content-row>
      </menu-content-container>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import hideOnMobile from "./components/hideOnMobile.vue";
  import paddingForm from "./paddingForm.vue";
  import hideOnDesktop from "./components/hideOnDesktop.vue";
  import borderRadiusForm from "./borderRadiusForm.vue";
  import ImageButtons from "./components/ImageButtons/ImageButtons.vue";

  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useMergeTagsStore } from "@/stores/mergeTags";
  import { useBlockAttr } from "@/composables/useBlockAttr";

  import { AligmentLeft, AligmentCenter, AligmentRight } from "@topol/icons";
  import {
    TButton,
    TButtonGroup,
    TGroupButton,
    TLabel,
    TSwitch,
    TSliderClassic as TSlider,
    TMergetagsAutocomplete as TAutoComplete,
  } from "@topol/twig-ui";

  import { ref, computed, onMounted } from "vue";

  const isDynamic = ref(false);
  const setDynamic = ref(false);
  const mutationEmptyString = ref("");

  const mjmlStore = useMjmlStore();
  const mergeTagsStore = useMergeTagsStore();
  const { str, nullable, bool } = useBlockAttr({ mode: "active" });

  // --- Composable bindings ---
  const padding = nullable("padding");
  const borderRadius = nullable("border-radius");
  const fluidOnMobile = bool("fluid-on-mobile", "true", "false");
  const align = str("align", "center");

  const src = computed({
    get() {
      if (mutationEmptyString.value === "src") {
        return "";
      }
      return mjmlStore.getActiveBlockAttrString("src");
    },
    set(val: string) {
      if ((!val || val.trim() === "") && mjmlStore.mjml.langs) {
        // If the value is empty, we set the mutationEmptyString to true
        // to avoid immediate display of the value from the primary mutation.
        mutationEmptyString.value = "src";
      } else {
        mutationEmptyString.value = "";
      }
      mjmlStore.updateBlockAttribute({
        prop: "src",
        value: val.trim(),
      });
    },
  });

  const disabledSrc = computed(() => {
    return mjmlStore.getActiveBlockPropDisabled("src");
  });

  const href = computed({
    get() {
      if (mutationEmptyString.value === "href") {
        return "";
      }
      return mjmlStore.getActiveBlockAttrString("href");
    },
    set(val: string) {
      if ((!val || val.trim() === "") && mjmlStore.mjml.langs) {
        // If the value is empty, we set the mutationEmptyString to true
        // to avoid immediate display of the value from the primary mutation.
        mutationEmptyString.value = "href";
      } else {
        mutationEmptyString.value = "";
      }
      mjmlStore.updateBlockAttribute({
        prop: "href",
        value: val.trim(),
      });
    },
  });

  const disabledHref = computed(() => {
    return mjmlStore.getActiveBlockPropDisabled("href");
  });

  const alt = computed({
    get() {
      if (mutationEmptyString.value === "alt") {
        return "";
      }
      return mjmlStore.getActiveBlockAttrString("alt");
    },
    set(val: string) {
      if (!val && mjmlStore.mjml.langs) {
        // If the value is empty, we set the mutationEmptyString to true
        // to avoid immediate display of the value from the primary mutation.
        mutationEmptyString.value = "alt";
      } else {
        mutationEmptyString.value = "";
      }
      mjmlStore.updateBlockAttribute({
        prop: "alt",
        value: val,
      });
    },
  });

  const disabledAlt = computed(() => {
    return mjmlStore.getActiveBlockPropDisabled("alt");
  });

  const disabledFluid = computed(() => {
    return mjmlStore.getActiveBlockPropDisabled("fluid-on-mobile");
  });

  const title = computed({
    get() {
      if (mutationEmptyString.value === "title") {
        return "";
      }
      return mjmlStore.getActiveBlockAttrString("title");
    },
    set(val: string) {
      if (!val && mjmlStore.mjml.langs) {
        // If the value is empty, we set the mutationEmptyString to true
        // to avoid immediate display of the value from the primary mutation.
        mutationEmptyString.value = "title";
      } else {
        mutationEmptyString.value = "";
      }
      mjmlStore.updateBlockAttribute({
        prop: "title",
        value: val,
      });
    },
  });

  const disabledTitle = computed(() => {
    return mjmlStore.getActiveBlockPropDisabled("title");
  });

  const disabledAlign = computed(() => {
    return mjmlStore.getActiveBlockPropDisabled("align");
  });

  function fixWidth(newWidth?: number) {
    mjmlStore.updateBlockAttribute({
      prop: "width",
      value: newWidth,
    });
  }

  const width = computed({
    get() {
      const width = mjmlStore.getActiveBlockAttrInt("width");
      if (width !== undefined) {
        if (isNaN(width)) {
          return mjmlStore.getActiveBlockAttrInt("containerWidth");
        }

        if (width > mjmlStore.getActiveBlockAttrInt("containerWidth")) {
          return mjmlStore.getActiveBlockAttrInt("containerWidth");
        }

        if (width === 0) {
          fixWidth();
          return mjmlStore.getActiveBlockAttrInt("containerWidth");
        }

        if (width < 30) {
          fixWidth(30);
          return 30;
        }

        return mjmlStore.getActiveBlockAttrInt("width");
      }

      return mjmlStore.getActiveBlockAttrInt("containerWidth");
    },
    set(val: number) {
      if (val !== width.value && val >= 30) {
        mjmlStore.updateBlockAttribute({
          prop: "width",
          value: val,
        });
      }
    },
  });

  const disabledWidth = computed(() => {
    return mjmlStore.getActiveBlockPropDisabled("width");
  });

  const containerWidth = computed(() => {
    return mjmlStore.getActiveBlockAttrInt("containerWidth");
  });

  const disabledPadding = computed(() => {
    return mjmlStore.getActiveBlockPropDisabled("padding");
  });

  function parseHref() {
    mutationEmptyString.value = "";
    const syntax = mergeTagsStore.smartMergeTags?.syntax || {
      start: "*|",
      end: "|*",
    };
    const startMergeTagSyntax = syntax.start || "*|";
    const startsWithSpecialChar = /^[^a-zA-Z0-9]/.test(href.value);
    if (
      href.value &&
      !href.value.startsWith("mailto") &&
      !href.value.startsWith("tel") &&
      !href.value.startsWith(startMergeTagSyntax) &&
      !startsWithSpecialChar &&
      !href.value.startsWith("http")
    ) {
      href.value = "http://" + href.value;
    }
  }

  onMounted(() => {
    if (mjmlStore.getActiveBlockAttrString("dynamicSrc")) {
      isDynamic.value = true;
    }
  });
</script>
